import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { CONTACT_API_URL as API_URL, COURSE_API_URL } from './lib/api-config';
import LoadingSpinner from './components/ui/LoadingSpinner';
import AlertMessage from './components/ui/AlertMessage';

const api = axios.create({ withCredentials: true });

function Contacts() {
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingContact, setEditingContact] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Booking history per contact (keyed by email)
  const [bookingHistory, setBookingHistory] = useState({});
  const [expandedBookings, setExpandedBookings] = useState({});

  // Tags state
  const [tags, setTags] = useState([]);
  const [filterTagId, setFilterTagId] = useState('');
  const [showTagManager, setShowTagManager] = useState(false);
  const [newTagName, setNewTagName] = useState('');
  const [newTagColor, setNewTagColor] = useState('#3B82F6');
  const [tagModalContact, setTagModalContact] = useState(null);

  // Formulärdata
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    company: '',
    status: 'Lead',
    notes: ''
  });

  // Hämta alla kontakter och taggar när sidan laddas
  useEffect(() => {
    fetchContacts();
    fetchTags();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Hämta kontakter från API
  const fetchContacts = async () => {
    try {
      setError('');
      const url = filterTagId
        ? `${API_URL}/contacts?tagId=${filterTagId}`
        : `${API_URL}/contacts`;
      const response = await api.get(url);
      setContacts(response.data.data);
      setLoading(false);
    } catch (error) {
      console.error('Fel vid hämtning av kontakter:', error);
      setError('Kunde inte hämta kontakter. Kontrollera din anslutning.');
      setLoading(false);
    }
  };

  // Hämta taggar
  const fetchTags = async () => {
    try {
      const response = await api.get(`${API_URL}/tags`);
      setTags(response.data.data);
    } catch (error) {
      console.error('Fel vid hämtning av taggar:', error);
    }
  };

  // Skapa ny tagg
  const handleCreateTag = async (e) => {
    e.preventDefault();
    if (!newTagName.trim()) return;

    try {
      await api.post(`${API_URL}/tags`, {
        name: newTagName.trim(),
        color: newTagColor
      });
      setNewTagName('');
      setNewTagColor('#3B82F6');
      fetchTags();
      setSuccess('Tagg skapad');
      setTimeout(() => setSuccess(''), 3000);
    } catch (error) {
      setError(error.response?.data?.message || 'Kunde inte skapa tagg');
    }
  };

  // Ta bort tagg
  const handleDeleteTag = async (tagId) => {
    if (!window.confirm('Ta bort denna tagg? (Tas bort från alla kontakter)')) return;

    try {
      await api.delete(`${API_URL}/tags/${tagId}`);
      fetchTags();
      fetchContacts();
      setSuccess('Tagg borttagen');
      setTimeout(() => setSuccess(''), 3000);
    } catch (error) {
      setError('Kunde inte ta bort tagg');
    }
  };

  // Lägg till tagg på kontakt
  const handleAddTagToContact = async (contactId, tagId) => {
    try {
      await api.post(`${API_URL}/contacts/${contactId}/tags`, { tagIds: [tagId] });
      fetchContacts();
    } catch (error) {
      setError('Kunde inte lägga till tagg');
    }
  };

  // Ta bort tagg från kontakt
  const handleRemoveTagFromContact = async (contactId, tagId) => {
    try {
      await api.delete(`${API_URL}/contacts/${contactId}/tags/${tagId}`);
      fetchContacts();
    } catch (error) {
      setError('Kunde inte ta bort tagg');
    }
  };

  // Hämta bokningshistorik för en kontakt
  const fetchBookingHistory = async (email) => {
    if (bookingHistory[email]) {
      // Toggle expanded state
      setExpandedBookings(prev => ({ ...prev, [email]: !prev[email] }));
      return;
    }
    try {
      const response = await api.get(`${COURSE_API_URL}/bookings/by-email/${encodeURIComponent(email)}`);
      setBookingHistory(prev => ({ ...prev, [email]: response.data.data }));
      setExpandedBookings(prev => ({ ...prev, [email]: true }));
    } catch (error) {
      console.error('Kunde inte hämta bokningshistorik:', error);
    }
  };

  // Filter by tag effect
  useEffect(() => {
    fetchContacts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterTagId]);

  // Hantera formulärändringar
  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  // Spara kontakt (skapa ny eller uppdatera)
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    // Validera e-post
    if (!formData.email || !formData.email.includes('@')) {
      setError('Ange en giltig e-postadress');
      return;
    }

    try {
      if (editingContact) {
        // Uppdatera befintlig kontakt
        await api.put(`${API_URL}/contacts/${editingContact.id}`, formData);
        setSuccess('Kontakt uppdaterad');
      } else {
        // Skapa ny kontakt
        await api.post(`${API_URL}/contacts`, formData);
        setSuccess('Kontakt skapad');
      }

      // Återställ formulär och hämta uppdaterad lista
      resetForm();
      fetchContacts();
      setTimeout(() => setSuccess(''), 3000);
    } catch (error) {
      const message = error.response?.data?.error || error.response?.data?.message || 'Kunde inte spara kontakt';
      setError(message);
    }
  };

  // Återställ formulär
  const resetForm = () => {
    setFormData({
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
      company: '',
      status: 'Lead',
      notes: ''
    });
    setShowForm(false);
    setEditingContact(null);
  };

  // Redigera kontakt
  const handleEdit = (contact) => {
    setFormData({
      firstName: contact.firstName || '',
      lastName: contact.lastName || '',
      email: contact.email || '',
      phone: contact.phone || '',
      company: contact.company || '',
      status: contact.status || 'Lead',
      notes: contact.notes || ''
    });
    setEditingContact(contact);
    setShowForm(true);
    setError('');
  };

  // Ta bort kontakt
  const [deleting, setDeleting] = useState(null);
  const handleDelete = async (contact) => {
    const name = `${contact.firstName || ''} ${contact.lastName || ''}`.trim() || contact.email;
    if (window.confirm(`Är du säker på att du vill ta bort "${name}"? (GDPR)`)) {
      setDeleting(contact.id);
      try {
        await api.delete(`${API_URL}/contacts/${contact.id}`);
        setSuccess(`"${name}" borttagen`);
        fetchContacts();
        setTimeout(() => setSuccess(''), 3000);
      } catch (error) {
        setError('Kunde inte ta bort kontakt: ' + (error.response?.data?.error || error.message));
      } finally {
        setDeleting(null);
      }
    }
  };

  // Sök kontakter
  const handleSearch = async (e) => {
    const query = e.target.value;
    setSearchQuery(query);

    if (query.length > 2) {
      try {
        const response = await api.get(`${API_URL}/contacts/search/${query}`);
        setContacts(response.data.data);
      } catch (error) {
        console.error('Sökfel:', error);
        setError('Sökning misslyckades');
      }
    } else if (query === '') {
      fetchContacts();
    }
  };

  // Formatera datum
  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('sv-SE');
  };

  // Status-badge färg
  const getStatusClass = (status) => {
    const statusMap = {
      'Lead': 'lead',
      'Contacted': 'contacted',
      'Qualified': 'qualified',
      'Proposal': 'proposal',
      'Customer': 'customer',
      'Inactive': 'inactive'
    };
    return statusMap[status] || 'lead';
  };

  return (
    <main className="App-main">
      {/* Felmeddelanden */}
      <AlertMessage error={error} success={success} />

      {/* Verktygsrad */}
      <div className="toolbar">
        <button
          className="btn btn-primary"
          onClick={() => {
            if (showForm) {
              resetForm();
            } else {
              setShowForm(true);
              setError('');
            }
          }}
        >
          {showForm ? 'Stäng' : '+ Ny kontakt'}
        </button>

        <button
          className="btn btn-secondary"
          onClick={() => setShowTagManager(!showTagManager)}
        >
          {showTagManager ? 'Stäng taggar' : 'Hantera taggar'}
        </button>

        <select
          className="tag-filter-select"
          value={filterTagId}
          onChange={(e) => setFilterTagId(e.target.value)}
        >
          <option value="">Alla taggar</option>
          {tags.map(tag => (
            <option key={tag.id} value={tag.id}>{tag.name}</option>
          ))}
        </select>

        <input
          type="text"
          placeholder="Sök kontakter (minst 3 tecken)..."
          className="search-input"
          value={searchQuery}
          onChange={handleSearch}
        />

        <div className="stats">
          Totalt: {contacts.length} kontakter
        </div>
      </div>

      {/* Tag Manager */}
      {showTagManager && (
        <div className="tag-manager">
          <h3>Hantera taggar</h3>
          <form onSubmit={handleCreateTag} className="tag-form">
            <input
              type="text"
              placeholder="Ny tagg..."
              value={newTagName}
              onChange={(e) => setNewTagName(e.target.value)}
            />
            <input
              type="color"
              value={newTagColor}
              onChange={(e) => setNewTagColor(e.target.value)}
              title="Välj färg"
            />
            <button type="submit" className="btn btn-success btn-small">Skapa</button>
          </form>
          <div className="tag-list">
            {tags.map(tag => (
              <div key={tag.id} className="tag-item">
                <span
                  className="tag"
                  style={{ backgroundColor: tag.color, color: '#fff' }}
                >
                  {tag.name}
                </span>
                <button
                  className="btn btn-danger btn-small"
                  onClick={() => handleDeleteTag(tag.id)}
                  title="Ta bort tagg"
                >
                  &times;
                </button>
              </div>
            ))}
            {tags.length === 0 && <p className="no-tags">Inga taggar skapade än</p>}
          </div>
        </div>
      )}

      {/* Formulär för ny/redigera kontakt */}
      {showForm && (
        <div className="form-container">
          <h2>{editingContact ? 'Redigera kontakt' : 'Lägg till ny kontakt'}</h2>
          <form onSubmit={handleSubmit}>
            <div className="form-row">
              <input
                type="text"
                name="firstName"
                placeholder="Förnamn"
                value={formData.firstName}
                onChange={handleInputChange}
              />
              <input
                type="text"
                name="lastName"
                placeholder="Efternamn"
                value={formData.lastName}
                onChange={handleInputChange}
              />
            </div>

            <div className="form-row">
              <input
                type="email"
                name="email"
                placeholder="E-post *"
                value={formData.email}
                onChange={handleInputChange}
                required
              />
              <input
                type="tel"
                name="phone"
                placeholder="Telefon"
                value={formData.phone}
                onChange={handleInputChange}
              />
            </div>

            <input
              type="text"
              name="company"
              placeholder="Företag"
              value={formData.company}
              onChange={handleInputChange}
              className="full-width"
            />

            <div className="form-row">
              <select
                name="status"
                value={formData.status}
                onChange={handleInputChange}
                className="status-select"
              >
                <option value="Lead">Lead - Potentiell kund</option>
                <option value="Contacted">Kontaktad</option>
                <option value="Qualified">Kvalificerad</option>
                <option value="Proposal">Offert skickad</option>
                <option value="Customer">Kund</option>
                <option value="Inactive">Inaktiv</option>
              </select>
            </div>

            <textarea
              name="notes"
              placeholder="Anteckningar..."
              value={formData.notes}
              onChange={handleInputChange}
              rows="3"
              className="full-width"
            />

            <div className="form-actions">
              <button type="submit" className="btn btn-success">
                {editingContact ? 'Uppdatera' : 'Spara'}
              </button>
              <button
                type="button"
                className="btn btn-secondary"
                onClick={resetForm}
              >
                Avbryt
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Kontaktlista */}
      {loading ? (
        <LoadingSpinner message="Laddar kontakter..." />
      ) : contacts.length === 0 ? (
        <div className="no-contacts">
          <p>Inga kontakter hittades</p>
          {searchQuery ? (
            <p>Försök med en annan sökning</p>
          ) : (
            <p>Klicka på "+ Ny kontakt" för att börja.</p>
          )}
        </div>
      ) : (
        <div className="contacts-table-wrapper">
          <table className="contacts-table">
            <thead>
              <tr>
                <th>Namn</th>
                <th>E-post</th>
                <th>Företag</th>
                <th>Status</th>
                <th>Taggar</th>
                <th>Åtgärder</th>
              </tr>
            </thead>
            <tbody>
              {contacts.map(contact => (
                <React.Fragment key={contact.id}>
                  <tr className={deleting === contact.id ? 'deleting' : ''}>
                    <td>
                      <strong>{contact.firstName} {contact.lastName}</strong>
                      {contact.phone && <div className="contact-phone-sub">{contact.phone}</div>}
                    </td>
                    <td><span className="email-cell" title={contact.email}>{contact.email}</span></td>
                    <td>{contact.company || '-'}</td>
                    <td>
                      <span className={`status ${getStatusClass(contact.status)}`}>
                        {contact.status}
                      </span>
                    </td>
                    <td>
                      <div className="contact-tags-inline">
                        {contact.tags?.map(tag => (
                          <span
                            key={tag.id}
                            className="tag"
                            style={{ backgroundColor: tag.color, color: '#fff' }}
                          >
                            {tag.name}
                            <button
                              className="tag-remove"
                              onClick={() => handleRemoveTagFromContact(contact.id, tag.id)}
                              title="Ta bort tagg"
                            >
                              &times;
                            </button>
                          </span>
                        ))}
                        <button
                          className="btn-add-tag"
                          onClick={() => setTagModalContact(tagModalContact?.id === contact.id ? null : contact)}
                          title="Lägg till tagg"
                        >
                          +
                        </button>
                        {tagModalContact?.id === contact.id && (
                          <div className="tag-dropdown">
                            {tags
                              .filter(t => !contact.tags?.some(ct => ct.id === t.id))
                              .map(tag => (
                                <button
                                  key={tag.id}
                                  className="tag-option"
                                  style={{ borderLeft: `4px solid ${tag.color}` }}
                                  onClick={() => {
                                    handleAddTagToContact(contact.id, tag.id);
                                    setTagModalContact(null);
                                  }}
                                >
                                  {tag.name}
                                </button>
                              ))}
                            {tags.filter(t => !contact.tags?.some(ct => ct.id === t.id)).length === 0 && (
                              <div className="no-tags-available">Inga fler taggar</div>
                            )}
                          </div>
                        )}
                      </div>
                    </td>
                    <td>
                      <div className="contact-actions-inline">
                        <button
                          className="btn btn-small btn-link"
                          onClick={() => fetchBookingHistory(contact.email)}
                          title="Visa bokningar"
                        >
                          {expandedBookings[contact.email] ? '▼' : '▶'}
                        </button>
                        <button
                          className="btn btn-small btn-secondary"
                          onClick={() => handleEdit(contact)}
                          title="Redigera"
                        >
                          ✏️
                        </button>
                        <button
                          className="btn btn-small btn-danger"
                          onClick={() => handleDelete(contact)}
                          disabled={deleting === contact.id}
                          title="Ta bort"
                        >
                          {deleting === contact.id ? '...' : '🗑️'}
                        </button>
                      </div>
                    </td>
                  </tr>
                  {expandedBookings[contact.email] && bookingHistory[contact.email] && (
                    <tr className="booking-history-row">
                      <td colSpan="6">
                        <div className="booking-history-inline">
                          {bookingHistory[contact.email].length === 0 ? (
                            <span className="no-bookings">Inga bokningar</span>
                          ) : (
                            bookingHistory[contact.email].map(booking => (
                              <div
                                key={booking.id}
                                className={`booking-item ${booking.status === 'Cancelled' ? 'cancelled' : ''}`}
                              >
                                <span className="booking-course">{booking.courseName}</span>
                                <span className="booking-date">
                                  {booking.courseDate ? new Date(booking.courseDate).toLocaleDateString('sv-SE') : '-'}
                                </span>
                                <span className={`booking-status status-${booking.status?.toLowerCase()}`}>
                                  {booking.status === 'Cancelled' ? 'Avbokad' : booking.status === 'Confirmed' ? 'Bekräftad' : booking.status}
                                </span>
                                {booking.status === 'Cancelled' && booking.cancellationReason && (
                                  <span className="cancellation-reason" title={booking.cancellationReason}>
                                    ({booking.cancellationReason})
                                  </span>
                                )}
                              </div>
                            ))
                          )}
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </main>
  );
}

export default Contacts;
